import {
  MyEquityWidget,
  MyKpiWidget,
  MyPnlCalendarWidget,
  MyTradesWidget,
  MyPositionsWidget,
  MyAllocationWidget,
  MyLiveAccountsWidget,
  MyVsStrategyWidget,
  MyInsightsWidget,
  MY_KPI_OPTIONS,
} from './MyDataWidgets';
import { ALL_ACCOUNTS, MY_RANGE_OPTIONS, myAccountOptions } from './myDataApi';
import {
  TwTodayWidget, TwAccountsWidget, TwDailyPnlWidget, TwCalendarWidget, TwAlertsWidget,
} from './TradeWatcherWidgets';

/**
 * "My accounts" widgets: the user's own data (My Portfolio + live Trade Watcher).
 * Same entry shape as widgetRegistry.js. Account options come from
 * myDataApi.loadMyAccounts(), loaded when the dashboard opens.
 */

const ACCOUNT_FIELD = {
  key: 'account',
  label: 'Account',
  type: 'select',
  optionsProvider: () => myAccountOptions(),
  defaultValue: ALL_ACCOUNTS,
};
const RANGE_FIELD = (fallback) => ({
  key: 'range', label: 'Period', type: 'select', options: MY_RANGE_OPTIONS, defaultValue: fallback,
});

const MY_WIDGET_REGISTRY = {
  myEquity: {
    label: 'My equity curve',
    description: 'Your account value over time: one account, a broker, or all combined.',
    component: MyEquityWidget,
    defaultSize: { w: 8, h: 5 },
    minSize: { w: 4, h: 3 },
    configFields: () => [ACCOUNT_FIELD, RANGE_FIELD('3M')],
    defaultConfig: () => ({ account: ALL_ACCOUNTS, range: '3M' }),
  },
  myKpi: {
    label: 'My KPI card',
    description: 'One number from your accounts: net P&L, today, winning days, drawdown…',
    component: MyKpiWidget,
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
    configFields: () => [
      { key: 'metric', label: 'Metric', type: 'select', options: MY_KPI_OPTIONS, defaultValue: 'netPnl' },
      ACCOUNT_FIELD,
      RANGE_FIELD('3M'),
    ],
    defaultConfig: () => ({ metric: 'netPnl', account: ALL_ACCOUNTS, range: '3M' }),
  },
  myPnlCalendar: {
    label: 'My P&L calendar',
    description: 'Your daily profit and loss by month.',
    component: MyPnlCalendarWidget,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
    configFields: () => [ACCOUNT_FIELD],
    defaultConfig: () => ({ account: ALL_ACCOUNTS }),
  },
  myTrades: {
    label: 'My recent trades',
    description: 'Your most recent closed trades across your accounts, whenever they happened.',
    component: MyTradesWidget,
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    configFields: () => [ACCOUNT_FIELD],
    defaultConfig: () => ({ account: ALL_ACCOUNTS }),
  },
  myInsights: {
    label: 'Insights from my trades',
    description: 'Patterns in your own closed trades: best and worst days, economic-release days, options strategies.',
    component: MyInsightsWidget,
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  myPositions: {
    label: 'My open positions',
    description: 'What you hold right now, with unrealized P&L.',
    component: MyPositionsWidget,
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 4, h: 3 },
    configFields: () => [ACCOUNT_FIELD],
    defaultConfig: () => ({ account: ALL_ACCOUNTS }),
  },
  myAllocation: {
    label: 'My allocation',
    description: 'How your money is split across brokers or accounts.',
    component: MyAllocationWidget,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 4 },
    configFields: () => [{
      key: 'groupBy', label: 'Group by', type: 'select', defaultValue: 'broker',
      options: [{ value: 'broker', label: 'Broker' }, { value: 'account', label: 'Account' }],
    }],
    defaultConfig: () => ({ groupBy: 'broker' }),
  },
  myLiveAccounts: {
    label: 'My live NinjaTrader accounts',
    description: 'Today’s P&L and connection for your NinjaTrader accounts (Trade Watcher).',
    component: MyLiveAccountsWidget,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  // Trade Watcher (NinjaTrader via the RiskLo Tally add-on): their own picker group.
  twToday: {
    group: 'tradeWatcher',
    label: 'Trade Watcher today',
    description: 'Running P&L, connected and naked accounts, open positions and an all-clear check, live.',
    component: TwTodayWidget,
    defaultSize: { w: 12, h: 2 },
    minSize: { w: 6, h: 2 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  twDailyPnl: {
    group: 'tradeWatcher',
    label: 'Trade Watcher daily P&L',
    description: 'Each day’s P&L as bars with the running total, win days, best and worst day and your streak.',
    component: TwDailyPnlWidget,
    defaultSize: { w: 8, h: 5 },
    minSize: { w: 4, h: 4 },
    configFields: () => [{
      key: 'days', label: 'Period', type: 'select', defaultValue: 90,
      options: [{ value: 30, label: '1 month' }, { value: 90, label: '3 months' }, { value: 180, label: '6 months' }, { value: 365, label: '1 year' }],
    }],
    defaultConfig: () => ({ days: 90 }),
  },
  twAccounts: {
    group: 'tradeWatcher',
    label: 'Trade Watcher accounts',
    description: 'Every NinjaTrader account: day, open and running P&L, position and status, problems first.',
    component: TwAccountsWidget,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 3 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  twCalendar: {
    group: 'tradeWatcher',
    label: 'Trade Watcher calendar',
    description: 'Your NinjaTrader daily P&L by month, with weekly totals.',
    component: TwCalendarWidget,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  twAlerts: {
    group: 'tradeWatcher',
    label: 'Trade Watcher alerts',
    description: 'Recent naked positions, disconnects and auto-closes on your accounts.',
    component: TwAlertsWidget,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
    configFields: () => [],
    defaultConfig: () => ({}),
  },
  myVsStrategy: {
    label: 'My account vs a strategy',
    description: 'Your P&L next to a strategy’s, over the same days.',
    component: MyVsStrategyWidget,
    defaultSize: { w: 8, h: 5 },
    minSize: { w: 4, h: 3 },
    /** Needs company strategies: hidden for people with no company. */
    needsCompanyStrategies: true,
    configFields: (strategiesProvider) => [
      ACCOUNT_FIELD,
      RANGE_FIELD('3M'),
      { key: 'strategy', label: 'Strategy', type: 'select', required: true, optionsProvider: strategiesProvider },
      { key: 'contractType', label: 'Contract type', type: 'select', defaultValue: 'NQ', options: [{ value: 'NQ', label: 'NQ' }, { value: 'MNQ', label: 'MNQ' }] },
      { key: 'contractSize', label: 'Contracts', type: 'number', min: 1, max: 100, defaultValue: 1 },
    ],
    defaultConfig: () => ({ account: ALL_ACCOUNTS, range: '3M', strategy: '', contractType: 'NQ', contractSize: 1 }),
  },
};

export const MY_WIDGET_TYPES = Object.entries(MY_WIDGET_REGISTRY).map(([type, meta]) => ({
  type, label: meta.label, description: meta.description, needsCompanyStrategies: Boolean(meta.needsCompanyStrategies),
  group: meta.group || 'mine',
}));

/** Trade Watcher widget types (their own picker group and dashboard prompt). */
export const TRADE_WATCHER_WIDGET_TYPES = MY_WIDGET_TYPES.filter((t) => t.group === 'tradeWatcher').map((t) => t.type);
export const isTradeWatcherWidgetType = (type) => TRADE_WATCHER_WIDGET_TYPES.includes(type);

export function getMyWidgetMeta(type) {
  return MY_WIDGET_REGISTRY[type] || null;
}

export function isMyWidgetType(type) {
  return Boolean(type && MY_WIDGET_REGISTRY[type]);
}

export default MY_WIDGET_REGISTRY;
